import React, { useDeferredValue, useState, useRef, useEffect } from "react";
import NoRecords from "../../components/alerts/NoRecords";
import OrganizationAutocompleteDropdown from "../../components/OrganizationAutocompleteDropdown";
import Button from "../../components/ui/Button";
import OrganizationResult from "./OrganizationResult";
import Select from "react-select";
import {
    filterOrganizationsByName,
    filterOrganizationsByPolicy,
} from "../../hooks/useOrganizationSearch";
import OrganizationRepository from "../../repositories/OrganizationRepository";
import {
    addLastSelectedOrganization,
    addSelectedOrganization,
} from "../../slices/organizationSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import GTM from "../../GTM";
import {
    addSelectedOrganizationForReports,
    setSelectedOrgainzationsIdForReports,
    setSelectedRelatedOrganizationsForReports,
} from "../../slices/viewReportSlice";
import {
    setPreferenceChanged,
    setOrganisationPreference,
} from "../../slices/preferenceSlice";
import PropTypes from "prop-types";

const list = [
    { value: "party/policy", label: "Party ID / Policy Number" },
    { value: "organizationName", label: "Organization" },
];

function OrganizationSearch({ organizations, policies }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const gtm = new GTM(null);

    const [searchType, setSearchType] = useState(list[1]);
    const [searchValue, setSearchValue] = useState("");
    const deferredSearchValue = useDeferredValue(searchValue);

    const { organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );
    const { organizationPreference } = useSelector(
        (state) => state.preferenceSlice
    );
    // setOrganisationPreference stores a new array instance on every dispatch, so depending on the
    // array itself re-fires effects that themselves re-dispatch it. Depend on its value instead.
    const organizationPreferenceKey = Array.isArray(organizationPreference)
        ? organizationPreference.join(",")
        : String(organizationPreference ?? "");
    const [filteredOrganizations, setFilteredOrganizations] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [dropdownOrganizations, setDropdownOrganizations] = useState([]);
    const [filterTimestamp, setFilterTimestamp] = useState(null);

    const organizationInputRef = useRef();

    const gtmEvent = (category, action, label, attribute3, attribute4) => {
        gtm.Event(
            "search",
            category,
            action,
            label,
            "search",
            "",
            attribute3,
            attribute4
        );
    };

    const submitForm = (e) => {
        e.preventDefault();

        setIsFiltered(true);
        setDropdownOrganizations([]);
        setFilterTimestamp(new Date().getTime());

        gtmEvent(
            "button",
            "click",
            "search",
            "field_interaction",
            searchType.label.toLowerCase()
        );

        if (searchType?.value === "organizationName") {
            setFilteredOrganizations(
                filterOrganizationsByName(organizations, searchValue)
            );
        } else {
            setFilteredOrganizations(
                filterOrganizationsByPolicy(
                    organizations,
                    policies,
                    searchValue
                )
            );
        }
    };

    const resetForm = () => {
        setSearchType(list[1]);
        setSearchValue("");
        setDropdownOrganizations([]);
        setFilteredOrganizations([]);
        setIsFiltered(false);
        gtmEvent("button", "click", "clear", "field_interaction", "");
    };

    const handleSearchTypeUpdate = (type) => {
        setSearchType(type);
        setSearchValue("");
        gtmEvent(
            "dropdown",
            "select",
            "select organization / policy",
            "field_interaction",
            `${type.label.toLocaleLowerCase()}`
        );
    };

    const handleSearchValueChange = ({ target: input }) => {
        setSearchValue(input.value);
    };

    const handleTextboxFocus = () => {
        let label = "";
        if (searchType.value === "organizationName") {
            label = "Enter Organization";
        } else {
            label = "Enter Party ID / Policy Number";
        }
    };

    const handleTextboxBlurFocus = () => {
        gtmEvent(
            "textbox",
            "interact",
            searchType.label.toLowerCase(),
            "field_interaction"
        );
    };

    const getButtonDisabledStatus = () => {
        return searchValue.trim() === "";
    };

    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
    } = useSelector((state) => state.reportOrganization);

    useEffect(() => {
        if (searchType?.value === "organizationName") {
            setDropdownOrganizations(
                filterOrganizationsByName(organizations, deferredSearchValue)
            );
        }
    }, [deferredSearchValue]);

    // organization saved in preferene
    useEffect(() => {
        if (organizationPreference && organizationPreference !== undefined) {
            const preferenceStoredOrg =
                OrganizationRepository.getRelatedOrganizationsFromIds(
                    allOrganizations,
                    organizationPreference
                );

            if (preferenceStoredOrg.length > 0) {
                dispatch(
                    addSelectedOrganization({
                        id: preferenceStoredOrg[0].ORGANIZATIONID,
                        organization: preferenceStoredOrg[0],
                        related:
                            preferenceStoredOrg[0]?.ORGANIZATIONGROUPID !== null
                                ? preferenceStoredOrg
                                : [],
                    })
                );

                dispatch(addLastSelectedOrganization(preferenceStoredOrg[0]));
                setIsFiltered(false);
                dispatch(
                    addSelectedOrganizationForReports({
                        id: preferenceStoredOrg[0].ORGANIZATIONID,
                        organization: preferenceStoredOrg[0],
                        related:
                            preferenceStoredOrg[0]?.ORGANIZATIONGROUPID !== null
                                ? preferenceStoredOrg
                                : [],
                    })
                );

                dispatch(
                    setSelectedOrgainzationsIdForReports(
                        preferenceStoredOrg[0].ORGANIZATIONID
                    )
                );

                navigate(
                    `/organization/${preferenceStoredOrg[0].ORGANIZATIONID}/employees/search`,
                    { replace: true }
                );
            }
        }
    }, [organizationPreferenceKey]);

    useEffect(() => {
        const selected = [];
        if (
            organizationPreference &&
            organizationPreference.length > 0 &&
            organizationPreference.toString() !== "" &&
            selectedRelatedOrganizations.length > 0
        ) {
            selectedRelatedOrganizations.forEach((item) => {
                const option = {
                    value: item.ORGANIZATIONID,
                    label: item.ORGNIZATIONNAME,
                    organizationIDISF: item.ORGANIZATIONIDISF,
                    organizationGroupId: item.ORGANIZATIONGROUPID,
                };

                if (
                    organizationPreference.includes(
                        String(item.ORGANIZATIONIDISF)
                    )
                ) {
                    selected.push(option);
                }
            });
        }
        if (selected.length > 0) {
            dispatch(setSelectedRelatedOrganizationsForReports(selected));
        }
        const option =
            userSelectedOrganizationReports?.[organizationPreference]
                ?.organization;
        if (option !== undefined) {
            selectOrganization(option);
        }
    }, [organizationPreferenceKey]);

    const setIsPreferenceChangedValue = (value) => {
        dispatch(setPreferenceChanged(value));
    };

    const selectOrganization = (organization) => {
        const related = OrganizationRepository.getRelatedOrganizations(
            allOrganizations,
            organization
        );

        dispatch(
            addSelectedOrganization({
                id: organization.ORGANIZATIONID,
                organization: organization,
                related,
            })
        );

        dispatch(addLastSelectedOrganization(organization));
        dispatch(
            setOrganisationPreference({
                organizationPreference: [organization.ORGANIZATIONIDISF],
            })
        );
        dispatch(
            addSelectedOrganizationForReports({
                id: organization.ORGANIZATIONID,
                organization: organization,
                related: related,
            })
        );

        dispatch(
            setSelectedOrgainzationsIdForReports(organization.ORGANIZATIONID)
        );

        setIsPreferenceChangedValue(false);

        navigate(
            `/organization/${organization.ORGANIZATIONID}/employees/search`
        );
    };

    return (
        <div>
            <form onSubmit={submitForm} className="md-px-4p">
                <div
                    className=" section-card bg-none row align-items-end"
                    style={{ margin: 0 }}
                >
                    <div
                        className="col-lg-4 col-md-4 col-sm-6 md-mb-20p "
                        style={{ paddingLeft: 0 }}
                    >
                        <div className="select-search">
                            <label htmlFor="orgpolicy" tabIndex={-1}>
                                Select Organization / Policy
                            </label>

                            <Select
                                className="select-dropdown"
                                placeholder="Please Select"
                                value={searchType}
                                options={list}
                                onChange={handleSearchTypeUpdate}
                                isSearchable={false}
                                id="orgpolicy"
                            />
                        </div>
                    </div>

                    <div className="col-lg-4 col-md-4 col-sm-6 md-mb-20p">
                        <div className="position-relative">
                            {searchType.value === "organizationName" ? (
                                <>
                                    <div ref={organizationInputRef}>
                                        <label htmlFor="org" tabIndex={-1}>
                                            Enter Organization
                                        </label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={searchValue}
                                            aria-autocomplete="list"
                                            aria-controls="org-list"
                                            onChange={handleSearchValueChange}
                                            onKeyUp={(event) => {
                                                if (event.key == "ArrowDown")
                                                    if (
                                                        document.getElementsByClassName(
                                                            "organisationclass"
                                                        ).length > 0
                                                    )
                                                        document
                                                            .getElementsByClassName(
                                                                "organisationclass"
                                                            )[0]
                                                            .focus();
                                            }}
                                            ref={(input) =>
                                                input && input.focus()
                                            }
                                            autoComplete="off"
                                            id="org"
                                            onFocus={handleTextboxFocus}
                                            onBlur={handleTextboxBlurFocus}
                                        />
                                    </div>

                                    {dropdownOrganizations.length > 0 && (
                                        <OrganizationAutocompleteDropdown
                                            organizations={
                                                dropdownOrganizations
                                            }
                                            hideOrganizationAutocompleteDropdown={() =>
                                                setDropdownOrganizations([])
                                            }
                                            organizationInputRef={
                                                organizationInputRef
                                            }
                                            selectOrganization={
                                                selectOrganization
                                            }
                                        />
                                    )}
                                </>
                            ) : (
                                <div>
                                    <label htmlFor="policy">
                                        Enter Party ID / Policy Number
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={searchValue}
                                        onChange={handleSearchValueChange}
                                        id="policy"
                                        onFocus={handleTextboxFocus}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-lg-2 col-md-2 col-sm-6 col-6">
                        <Button
                            onClick={submitForm}
                            disabled={getButtonDisabledStatus()}
                            text="Search"
                            icon="fa-magnifying-glass"
                            type="submit"
                        />
                    </div>

                    <div
                        className="col-lg-2 col-md-2 col-sm-6 col-6 pr-0p"
                        style={{ paddingRight: 0 }}
                    >
                        <Button
                            onClick={resetForm}
                            disabled={getButtonDisabledStatus()}
                            text="Clear"
                            icon="fa-xmark"
                            outline={true}
                        />
                    </div>
                </div>
            </form>

            <div className="my-30p">
                {isFiltered &&
                    (filteredOrganizations.length === 0 ? (
                        <NoRecords
                            timestamp={filterTimestamp}
                            page="search"
                            label="search"
                            attribute1="search"
                            attribute3="search_error"
                        />
                    ) : (
                        <OrganizationResult
                            organizations={filteredOrganizations}
                            selectOrganization={selectOrganization}
                        />
                    ))}
            </div>
        </div>
    );
}

OrganizationSearch.propTypes = {
    organizations: PropTypes.array,
    policies: PropTypes.array,
};

export default OrganizationSearch;
